/**
 * HTTP-backed implementation of `RenewalDataPort` (TG-4, m1-exposure-detail-
 * sourcing), over the published portal API route this feature's own TG-3
 * built: `GET /api/portal/account`. Mirrors `../renewal-summary-client.ts`'s
 * shape — see that file's own header comment for the outcome-mapping
 * rationale this file repeats without re-explaining it.
 *
 * ADR-20261018-1skz: gated by `whenSessionTokenHeld` — no request fires
 * while no session token is held.
 */
import { authenticatedFetch } from "../../portal-auth/authenticated-fetch";
import type { ReadOutcome } from "../outcome";
import type { AccountReadResult, RenewalDataPort } from "../renewal-data-port";
import { whenSessionTokenHeld } from "./session-gate";

const ACCOUNT_ROUTE = "/api/portal/account";

function failedOutcome(httpStatus: number, message: string): ReadOutcome<AccountReadResult> {
  return { status: "Failed", detail: { httpStatus, message } };
}

async function readAccountField(response: Response): Promise<AccountReadResult> {
  const body = (await response.json()) as { account?: AccountReadResult };
  if (body.account === undefined) {
    throw new Error(`GET ${ACCOUNT_ROUTE}: 200 response carried no readable "account" field.`);
  }
  return body.account;
}

async function performReadAccount(): Promise<ReadOutcome<AccountReadResult>> {
  let response: Response;
  try {
    response = await authenticatedFetch(ACCOUNT_ROUTE, { method: "GET" });
  } catch (error) {
    return failedOutcome(0, error instanceof Error ? error.message : String(error));
  }

  // A contributor session is refused 403 not_permitted at this route
  // (ADR-20261019-7wvr) -- this port carries no third outcome member for
  // it; it maps to Failed like any other non-200 status, distinguished
  // only by the carried httpStatus, exactly as every other status code is.
  if (response.status !== 200) {
    return failedOutcome(response.status, `GET ${ACCOUNT_ROUTE} returned ${response.status}.`);
  }

  try {
    const value = await readAccountField(response);
    return { status: "Succeeded", value };
  } catch (error) {
    return failedOutcome(response.status, error instanceof Error ? error.message : String(error));
  }
}

export function createHttpRenewalDataPort(): RenewalDataPort & { isSampleData: false } {
  return {
    isSampleData: false,
    async readAccount(): Promise<ReadOutcome<AccountReadResult>> {
      return whenSessionTokenHeld(performReadAccount);
    },
  };
}
